import React from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { Tooltip } from "recharts";
import { Typography, useMediaQuery, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRangeSelectControl, useChartColors } from "@hooks";
import { RangeSelector } from "../../common";
import {
  Chart,
  MainLine,
  GridAndAxis,
  Figure,
  ChartLayout,
  useTickFormat,
  CustomTooltip,
} from "../BaseChartTools";

const ChartArea = styled.div`
  width: 100%;
  height: 280px;
`;

export interface AccountChartDataWithDate {
  date: Date;
  value: number;
  areaValue: [number, number];
}

export interface AccountChartProps {
  title: string;
  data: AccountChartDataWithDate[];
  className?: string;
  onSelect?: (type: IntervalType, value?: RangeType) => void;
  isFetching?: boolean;
  timeBrushData: { date: Date; value: number }[];
}

export const AccountChart = ({
  data,
  title,
  className,
  onSelect,
  isFetching,
}: AccountChartProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const chartColors = useChartColors("securitiesChart");
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const tickInterval = Math.floor(data.length / (isXs ? 5 : 12));

  const {
    range,
    intervalType,
    isTimeBrushOpen,
    setIsTimeBrushOpen,
    customIntervalSelect,
    selectInterval,
    isCustomRangeSelectOpened,
  } = useRangeSelectControl({
    onSelect,
    firstDate: data[0] ? data[0].date : new Date(),
  });
  const tickFormat = useTickFormat(range.startDate, range.endDate);

  return (
    <ChartLayout
      useCard={false}
      onOpenTimeBrushClick={() => setIsTimeBrushOpen((current) => !current)}
      showTimeBrush={isTimeBrushOpen}
      isFetching={isFetching}
      className={className}
      intervalSelector={
        <RangeSelector
          compact={isXs}
          range={intervalType === "custom" ? range : undefined}
          intervalType={intervalType}
          onSelect={selectInterval}
          onCustomSelect={customIntervalSelect}
        />
      }
      figure={
        <Figure title={<Typography variant="h4">{title}</Typography>}>
          <Typography variant="body4" color="textSecondary">
            {t("accountChart.subtitle")}
          </Typography>
        </Figure>
      }
      isContentFaded={isCustomRangeSelectOpened}
    >
      <ChartArea>
        <Chart data={data}>
          {GridAndAxis({
            theme,
            tickInterval,
            xDataKey: "date",
            tickFormat,
            hideYAxis: isXs,
          })}

          <Tooltip
            cursor={false}
            content={<CustomTooltip fieldName="value" />}
          />
          {MainLine({
            dataKey: "value",
            dataAreaKey: "areaValue",
            chartColors,
          })}
        </Chart>
      </ChartArea>
    </ChartLayout>
  );
};
